/**
 * BATCH 24 PART 6 — Workflows 51-60: IT & Security
 * 10 elite production-standard workflow templates
 * Services: Okta, PagerDuty, Jira, Vault, AWS, GitHub, BambooHR, Google Workspace, SecurityScorecard, Microsoft Graph, Slack, Google Sheets, OpenAI
 *
 * Production enhancements in every workflow:
 * 1. Case stamping (node 2) — unique case_id + timestamps
 * 2. aiNodeV2 — confidence_score + evidence_summary + needs_human_review
 * 3. confidenceGate — routes high-confidence auto vs low-confidence human review
 * 4. verifyNode — read-back confirmation of executed actions
 * 5. exceptionNode / sheetsNode — structured logging or error handling
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED HELPERS — V2 FOR BATCH 24 ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 51-60: IT & SECURITY ============

export const ELITE_BATCH24_PART6: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 51. Elite: Access Request Processor ──
  "Elite: Access Request Processor": {
    nodes: [
      trigger("Slack: Access request submitted via command", "webhook_app", {
        source: "Slack",
        event: "slash_command.access_request",
        howItStarts:
          "Fires when an employee submits an access request via Slack slash command (e.g., /request-access app:Salesforce role:admin).",
        setup:
          "Configure Slack slash command /request-access to POST to webhook. Payload includes requesting user, target application, requested role, and business justification.",
        inputFields: [
          {
            name: "requester_email",
            type: "string",
            example: "jane@company.com",
          },
          { name: "requester_name", type: "string", example: "Jane Doe" },
          { name: "target_app", type: "string", example: "Salesforce" },
          { name: "requested_role", type: "string", example: "admin" },
          {
            name: "justification",
            type: "string",
            example:
              "Need admin access to configure custom objects for Q2 project",
          },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Access request",
        200,
        "Access Request Processor",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Validate role eligibility + risk assessment",
        360,
        "You are an IT security analyst evaluating access requests. Assess whether the requested role is appropriate for the user based on their current role, department, and existing permissions. Check for: separation of duties violations, excessive privilege requests, temporary vs permanent access needs, and compliance implications. Return JSON with eligible (boolean), risk_level ('low','medium','high','critical'), sod_conflict (boolean, true if separation of duties violation), recommended_scope ('as_requested','reduced_scope','time_limited','deny'), time_limit_days (number or 0 for permanent), policy_references (string citing relevant policies), and assessment_notes (2-3 sentences).",
        "Requester: {requester_name} ({requester_email}). Target: {target_app}. Role: {requested_role}. Justification: {justification}. Current permissions: {current_permissions}. Department: {department}.",
        [
          { field: "eligible", as: "eligible", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "sod_conflict", as: "sod_conflict", type: "string" },
          {
            field: "recommended_scope",
            as: "recommended_scope",
            type: "string",
          },
          { field: "time_limit_days", as: "time_limit_days", type: "number" },
          { field: "assessment_notes", as: "assessment_notes", type: "string" },
        ],
      ),
      n("4", "hitApi", "Okta: Fetch current user permissions", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{okta_domain}/api/v1/users/{requester_email}/appLinks",
          headers: [
            { key: "Authorization", value: "SSWS {okta_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "[0].label", customName: "current_apps", type: "string" },
            { name: "length", customName: "total_apps", type: "number" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Eligibility assessment confidence", 680),
      n("6", "hitApi", "Okta: Provision access to application", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/apps/{target_app_id}/users",
          headers: [
            { key: "Authorization", value: "SSWS {okta_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "id", value: "{okta_user_id}" },
            { key: "scope", value: "USER" },
            { key: "profile", value: '{"role":"{requested_role}"}' },
          ],
          variables: [
            { name: "id", customName: "assignment_id", type: "string" },
            { name: "status", customName: "assignment_status", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: Manager + Security review (low confidence)",
        840,
        "Access request needs review. Requester: {requester_name}. App: {target_app}. Role: {requested_role}. Risk: {risk_level}. SoD conflict: {sod_conflict}. Recommended: {recommended_scope}. Notes: {assessment_notes}. AI confidence: {ai_confidence}%.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Access granted in Okta",
        1000,
        "GET",
        "https://{okta_domain}/api/v1/apps/{target_app_id}/users/{okta_user_id}",
        [
          { key: "Authorization", value: "SSWS {okta_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          { name: "id", customName: "verified_assignment_id", type: "string" },
          { name: "status", customName: "verified_status", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Access provisioning verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Audit log access grant",
        1320,
        "AccessAudit",
        '[["{case_id}","{requester_email}","{target_app}","{requested_role}","{risk_level}","{sod_conflict}","{recommended_scope}","{ai_confidence}","{verified_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes Slack access requests, uses AI to validate role eligibility and assess risk, auto-provisions via Okta for low-risk high-confidence requests, routes elevated-risk requests to manager and security approval, verifies provisioning, and logs to audit sheet.",
  },

  // ── 52. Elite: Suspicious Login Analyzer ──
  "Elite: Suspicious Login Analyzer": {
    nodes: [
      trigger("Okta: Suspicious authentication event", "webhook_app", {
        source: "Okta",
        event: "user.session.start",
        howItStarts:
          "Fires when Okta detects a login event flagged as suspicious due to new device, impossible travel, Tor exit node, or anomalous behavior score.",
        setup:
          "Configure Okta System Log event hook for user.session.start events with anomaly detection flags. Ensure payload includes user ID, IP, geolocation, device info, and risk score.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "employee@company.com",
          },
          { name: "login_ip", type: "string", example: "185.220.101.42" },
          { name: "geo_city", type: "string", example: "Moscow" },
          { name: "geo_country", type: "string", example: "RU" },
          { name: "device_os", type: "string", example: "Linux" },
          { name: "risk_score", type: "number", example: "85" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Suspicious login",
        200,
        "Suspicious Login Analyzer",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Analyze anomaly signals with evidence",
        360,
        "You are a SOC Tier-1 analyst. Analyze the suspicious login event and determine threat level. Consider: impossible travel (compare with last known location), Tor/VPN exit nodes, new device fingerprint, login time anomalies, geo-reputation, and historical behavior. Return JSON with threat_level ('benign','suspicious','likely_compromise','confirmed_threat'), anomaly_signals (comma-separated list of detected signals), impossible_travel (boolean), tor_or_vpn (boolean), recommended_action ('allow','challenge_mfa','suspend_session','lock_account'), investigation_priority (1-5 where 1 is highest), and analyst_brief (3-4 sentence summary for SOC handoff).",
        "Login event. User: {user_email}. IP: {login_ip}. Location: {geo_city}, {geo_country}. Device: {device_os}. Okta risk score: {risk_score}. User login history: {login_history}.",
        [
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "anomaly_signals", as: "anomaly_signals", type: "string" },
          {
            field: "impossible_travel",
            as: "impossible_travel",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "investigation_priority",
            as: "investigation_priority",
            type: "number",
          },
          { field: "analyst_brief", as: "analyst_brief", type: "string" },
        ],
      ),
      n("4", "hitApi", "Okta: Fetch user login history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{okta_domain}/api/v1/users/{user_email}/sessions",
          headers: [
            { key: "Authorization", value: "SSWS {okta_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].login",
              customName: "last_login_time",
              type: "string",
            },
            {
              name: "[0].lastPasswordVerification",
              customName: "last_pw_verify",
              type: "string",
            },
            { name: "length", customName: "active_sessions", type: "number" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Threat analysis confidence", 680),
      emailNode(
        "6",
        "Email: Send 'Was this you?' verification",
        840,
        "{user_email}",
        "Security Alert: New login detected from {geo_city}, {geo_country}",
        "Hi,\\n\\nWe detected a login to your account from a new location.\\n\\nDetails:\\n- Location: {geo_city}, {geo_country}\\n- IP Address: {login_ip}\\n- Device: {device_os}\\n- Time: {started_at}\\n\\nWas this you?\\n\\n[YES, this was me] — https://security.yourapp.com/verify/{case_id}?action=confirm\\n[NO, secure my account] — https://security.yourapp.com/verify/{case_id}?action=lock\\n\\nIf you did not initiate this login, click the link above immediately to secure your account.\\n\\nSecurity Team",
      ),
      approvalNode(
        "7",
        "Approval: SOC analyst review (low confidence)",
        840,
        "Suspicious login needs SOC review. User: {user_email}. IP: {login_ip}. Location: {geo_city}, {geo_country}. Threat: {threat_level}. Signals: {anomaly_signals}. Impossible travel: {impossible_travel}. Priority: {investigation_priority}. Brief: {analyst_brief}. AI confidence: {ai_confidence}%.",
        4,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Alert email sent to user",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={user_email}&count=1",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Messages[0].Status",
            customName: "alert_email_status",
            type: "string",
          },
          {
            name: "Messages[0].MessageID",
            customName: "alert_message_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Alert delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log security incident",
        1320,
        "IncidentLog",
        '[["{case_id}","{user_email}","{login_ip}","{geo_city}","{geo_country}","{threat_level}","{anomaly_signals}","{recommended_action}","{ai_confidence}","{alert_email_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Catches suspicious Okta login events, uses AI to analyze anomaly signals and assess threat level, sends user verification emails for high-confidence detections, escalates uncertain cases to SOC analysts, verifies alert delivery, and logs to incident sheet.",
  },

  // ── 53. Elite: Vulnerability Triage Bot ──
  "Elite: Vulnerability Triage Bot": {
    nodes: [
      trigger("Webhook: Vulnerability scan results received", "webhook_app", {
        source: "Scanner",
        event: "scan.completed",
        howItStarts:
          "Fires when vulnerability scanner (e.g., Qualys, Nessus, Snyk) completes a scan and posts results via webhook.",
        setup:
          "Configure vulnerability scanner to POST results to webhook endpoint upon scan completion. Payload includes CVE list, affected assets, severity scores, and scan metadata.",
        inputFields: [
          { name: "scan_id", type: "string", example: "scan-2024-03-15-001" },
          { name: "scanner_type", type: "string", example: "Snyk" },
          { name: "total_findings", type: "number", example: "47" },
          { name: "critical_count", type: "number", example: "3" },
          { name: "high_count", type: "number", example: "12" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Vulnerability triage",
        200,
        "Vulnerability Triage Bot",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Dedupe + prioritize by exploitability",
        360,
        "You are a vulnerability management specialist. Analyze scan results to deduplicate findings, prioritize by real-world exploitability (not just CVSS), and create actionable remediation tickets. Consider: EPSS score (exploit prediction), CISA KEV catalog membership, asset criticality, network exposure, and compensating controls. Return JSON with unique_vulns (count after dedup), critical_exploitable (count of truly critical + exploitable), remediation_tickets (string summary of top 10 tickets to create with CVE, asset, priority, and recommended fix), false_positive_candidates (string listing likely false positives), sla_breach_risk (count of vulns that will breach SLA if not fixed within 7 days), and triage_summary (3 sentence overview).",
        "Scan ID: {scan_id}. Scanner: {scanner_type}. Total findings: {total_findings}. Critical: {critical_count}. High: {high_count}. Raw findings: {scan_findings}. NVD enrichment: {nvd_data}.",
        [
          { field: "unique_vulns", as: "unique_vulns", type: "number" },
          {
            field: "critical_exploitable",
            as: "critical_exploitable",
            type: "number",
          },
          {
            field: "remediation_tickets",
            as: "remediation_tickets",
            type: "string",
          },
          {
            field: "false_positive_candidates",
            as: "false_positive_candidates",
            type: "string",
          },
          { field: "sla_breach_risk", as: "sla_breach_risk", type: "number" },
          { field: "triage_summary", as: "triage_summary", type: "string" },
        ],
      ),
      n("4", "hitApi", "NVD: Enrich CVEs with exploitability data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://services.nvd.nist.gov/rest/json/cves/2.0?resultsPerPage=20&keywordSearch={top_cves}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "totalResults", customName: "nvd_results", type: "number" },
            { name: "vulnerabilities", customName: "nvd_data", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Triage accuracy confidence", 680),
      n("6", "hitApi", "Jira: Create remediation tickets", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{security_project}"},"summary":"[VULN] {top_cve} — {top_asset}","description":"Vulnerability remediation ticket auto-created by triage bot.\\n\\nCase: {case_id}\\nScan: {scan_id}\\nCritical exploitable: {critical_exploitable}\\nSLA breach risk: {sla_breach_risk}\\n\\nRemediation details:\\n{remediation_tickets}","issuetype":{"name":"Bug"},"priority":{"name":"High"},"labels":["vulnerability","auto-triage"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_ticket_id", type: "string" },
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: Security lead review (low confidence)",
        840,
        "Vulnerability triage needs review. Scan: {scan_id}. Unique vulns: {unique_vulns}. Critical exploitable: {critical_exploitable}. SLA breach risk: {sla_breach_risk}. False positive candidates: {false_positive_candidates}. Summary: {triage_summary}. AI confidence: {ai_confidence}%.",
        12,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Jira ticket created",
        1000,
        "GET",
        "https://{jira_domain}/rest/api/3/issue/{jira_ticket_key}",
        [
          { key: "Authorization", value: "Basic {jira_auth}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "fields.status.name",
            customName: "ticket_status",
            type: "string",
          },
          { name: "key", customName: "verified_ticket_key", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Ticket creation verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log triage results",
        1320,
        "VulnTriageLog",
        '[["{case_id}","{scan_id}","{scanner_type}","{unique_vulns}","{critical_exploitable}","{sla_breach_risk}","{false_positive_candidates}","{ai_confidence}","{verified_ticket_key}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Receives vulnerability scan results, uses AI to deduplicate and prioritize by real exploitability enriched with NVD data, auto-creates Jira remediation tickets for high-confidence triage, routes uncertain findings to security lead, verifies tickets, and logs results.",
  },

  // ── 54. Elite: Incident Response Orchestrator ──
  "Elite: Incident Response Orchestrator": {
    nodes: [
      trigger("Webhook: Incident report submitted", "webhook_app", {
        source: "Monitoring",
        event: "incident.created",
        howItStarts:
          "Fires when an incident is reported via monitoring alert (Datadog, PagerDuty) or manual submission through the incident form.",
        setup:
          "Configure monitoring tool webhooks and incident form to POST to this endpoint. Payload should include severity estimate, affected service, description, and reporter info.",
        inputFields: [
          {
            name: "incident_title",
            type: "string",
            example: "API latency spike in payment service",
          },
          { name: "reported_severity", type: "string", example: "high" },
          { name: "affected_service", type: "string", example: "payment-api" },
          {
            name: "reporter_email",
            type: "string",
            example: "oncall@company.com",
          },
          {
            name: "description",
            type: "string",
            example: "P99 latency jumped from 200ms to 5s. Error rate at 12%.",
          },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Incident response",
        200,
        "Incident Response Orchestrator",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Classify severity + select runbook",
        360,
        "You are an incident commander AI assistant. Classify the incident severity (SEV1-SEV4) based on customer impact, blast radius, and service criticality. Select the appropriate runbook and identify the right responder teams. Return JSON with classified_severity ('SEV1','SEV2','SEV3','SEV4'), customer_impact ('none','degraded','partial_outage','full_outage'), blast_radius ('single_service','multiple_services','platform_wide'), runbook_id (string matching internal runbook), required_teams (comma-separated team names), escalation_chain (string of roles to notify), estimated_ttm (estimated time to mitigate in minutes), and commander_brief (4-5 sentence situation report).",
        "Incident: {incident_title}. Reported severity: {reported_severity}. Service: {affected_service}. Description: {description}. Reporter: {reporter_email}.",
        [
          {
            field: "classified_severity",
            as: "classified_severity",
            type: "string",
          },
          { field: "customer_impact", as: "customer_impact", type: "string" },
          { field: "blast_radius", as: "blast_radius", type: "string" },
          { field: "runbook_id", as: "runbook_id", type: "string" },
          { field: "required_teams", as: "required_teams", type: "string" },
          { field: "commander_brief", as: "commander_brief", type: "string" },
        ],
      ),
      n("4", "hitApi", "PagerDuty: Page on-call responders", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pagerduty.com/incidents",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "From", value: "{reporter_email}" },
          ],
          body: [
            {
              key: "incident",
              value:
                '{"type":"incident","title":"{incident_title} [{classified_severity}]","service":{"id":"{pd_service_id}","type":"service_reference"},"urgency":"high","body":{"type":"incident_body","details":"{commander_brief}"}}',
            },
          ],
          variables: [
            {
              name: "incident.id",
              customName: "pd_incident_id",
              type: "string",
            },
            {
              name: "incident.html_url",
              customName: "pd_incident_url",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Severity classification confidence", 680),
      slackNode(
        "6",
        "Slack: Create incident war room channel",
        840,
        ":rotating_light: *INCIDENT WAR ROOM* — {classified_severity}\\n\\n*Title:* {incident_title}\\n*Service:* {affected_service}\\n*Impact:* {customer_impact} | Blast radius: {blast_radius}\\n\\n*Commander Brief:*\\n{commander_brief}\\n\\n*Runbook:* {runbook_id}\\n*Teams Required:* {required_teams}\\n*PagerDuty:* {pd_incident_url}\\n\\n*Case ID:* {case_id}\\n_All responders: join this channel immediately._",
      ),
      approvalNode(
        "7",
        "Approval: Incident commander review (low confidence)",
        840,
        "Incident classification needs commander review. Title: {incident_title}. AI classified: {classified_severity}. Customer impact: {customer_impact}. Blast radius: {blast_radius}. Teams: {required_teams}. Brief: {commander_brief}. AI confidence: {ai_confidence}%.",
        1,
        400,
      ),
      verifyNode(
        "8",
        "Verify: War room channel active",
        1000,
        "POST",
        "https://slack.com/api/conversations.list?types=public_channel&limit=1",
        [
          { key: "Authorization", value: "Bearer {slack_bot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        [
          { name: "ok", customName: "channel_created", type: "string" },
          {
            name: "channels[0].id",
            customName: "war_room_channel_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: War room creation verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log incident timeline",
        1320,
        "IncidentTimeline",
        '[["{case_id}","{incident_title}","{classified_severity}","{affected_service}","{customer_impact}","{blast_radius}","{required_teams}","{pd_incident_id}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Receives incident reports, uses AI to classify severity and select runbooks, pages on-call via PagerDuty, creates Slack war room channels for high-confidence classifications, routes uncertain cases to incident commander, verifies channel creation, and logs timeline.",
  },

  // ── 55. Elite: Secret Rotation Enforcer ──
  "Elite: Secret Rotation Enforcer": {
    nodes: [
      trigger("Schedule: Daily secret rotation audit", "schedule", {
        schedule: "Every day at 6:00 AM UTC",
        howItStarts:
          "Runs daily to identify secrets (API keys, tokens, certificates) that are overdue for rotation based on policy.",
        setup:
          "Configure daily schedule. Ensure Vault API access for secret metadata. Rotation policy: API keys every 90 days, service tokens every 180 days, TLS certificates 30 days before expiry.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: Secret rotation audit",
        200,
        "Secret Rotation Enforcer",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Identify overdue secrets by risk",
        360,
        "You are a secrets management specialist. Analyze the Vault inventory and identify secrets overdue for rotation. Prioritize by: blast radius if compromised, days past rotation deadline, secret type criticality, and whether the secret has been exposed in any known breach. Return JSON with total_secrets_audited (count), overdue_count, critical_overdue (count of secrets with high blast radius), overdue_list (string summary of top 10 with secret_path, days_overdue, owner, risk_rating), rotation_plan (string with recommended rotation order), and compliance_gap_days (average days past policy deadline).",
        "Vault secret inventory: {vault_inventory}. Rotation policy: API keys=90d, service tokens=180d, TLS certs=30d before expiry. Current date: {started_at}.",
        [
          {
            field: "total_secrets_audited",
            as: "total_secrets_audited",
            type: "number",
          },
          { field: "overdue_count", as: "overdue_count", type: "number" },
          { field: "critical_overdue", as: "critical_overdue", type: "number" },
          { field: "overdue_list", as: "overdue_list", type: "string" },
          { field: "rotation_plan", as: "rotation_plan", type: "string" },
          {
            field: "compliance_gap_days",
            as: "compliance_gap_days",
            type: "number",
          },
        ],
      ),
      n("4", "hitApi", "Vault: Fetch secret metadata inventory", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{vault_addr}/v1/secret/metadata?list=true",
          headers: [
            { key: "X-Vault-Token", value: "{vault_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.keys",
              customName: "vault_inventory",
              type: "string",
            },
            {
              name: "data.keys.length",
              customName: "total_secrets",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Rotation audit confidence", 680),
      emailNode(
        "6",
        "Email: Send rotation reminders to owners",
        840,
        "{security_team_email}",
        "Secret Rotation Required — {overdue_count} secrets overdue ({critical_overdue} critical)",
        "Security Team,\\n\\nDaily secret rotation audit completed. Case: {case_id}\\n\\n== Summary ==\\nTotal secrets audited: {total_secrets_audited}\\nOverdue for rotation: {overdue_count}\\nCritical (high blast radius): {critical_overdue}\\nAverage days past policy: {compliance_gap_days}\\n\\n== Overdue Secrets ==\\n{overdue_list}\\n\\n== Recommended Rotation Plan ==\\n{rotation_plan}\\n\\nPlease begin rotating critical secrets immediately.\\n\\nSecrets Management Automation",
      ),
      approvalNode(
        "7",
        "Approval: Security lead review (low confidence)",
        840,
        "Secret rotation audit needs review. Overdue: {overdue_count}. Critical: {critical_overdue}. Avg gap: {compliance_gap_days} days. Overdue list: {overdue_list}. AI confidence: {ai_confidence}%. Please verify findings before sending rotation reminders.",
        12,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Reminder emails sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={security_team_email}&count=1",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Messages[0].Status",
            customName: "reminder_status",
            type: "string",
          },
          {
            name: "Messages[0].MessageID",
            customName: "reminder_message_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Reminder delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log compliance audit",
        1320,
        "SecretComplianceLog",
        '[["{case_id}","{total_secrets_audited}","{overdue_count}","{critical_overdue}","{compliance_gap_days}","{rotation_plan}","{ai_confidence}","{reminder_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Daily audit scans Vault secret metadata, uses AI to identify overdue rotations prioritized by blast radius, sends owner reminders for high-confidence findings, routes uncertain audits to security lead, verifies delivery, and logs compliance data.",
  },

  // ── 56. Elite: SOC2 Evidence Collector ──
  "Elite: SOC2 Evidence Collector": {
    nodes: [
      trigger("Schedule: Monthly SOC2 evidence collection", "schedule", {
        schedule: "First Monday of each month at 7:00 AM UTC",
        howItStarts:
          "Runs monthly to collect and organize SOC2 audit evidence across AWS, GitHub, and HR systems against Trust Service Criteria.",
        setup:
          "Configure monthly schedule. Ensure API access to AWS CloudTrail, GitHub org settings, and HR system. Map evidence artifacts to SOC2 TSC criteria (CC1-CC9, availability, confidentiality, processing integrity, privacy).",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: SOC2 evidence collection",
        200,
        "SOC2 Evidence Collector",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Map artifacts to TSC criteria",
        360,
        "You are a SOC2 compliance specialist. Analyze the collected evidence artifacts and map them to Trust Service Criteria. Identify gaps where evidence is missing or insufficient. Return JSON with artifacts_collected (count), criteria_covered (count out of total TSC criteria), gap_count (number of criteria with missing/insufficient evidence), coverage_percentage (number 0-100), gap_details (string listing each gap with TSC reference, description, and suggested remediation), evidence_index (string formatted as a table mapping each artifact to its TSC criteria), and audit_readiness_score (1-100).",
        "AWS CloudTrail summary: {aws_evidence}. GitHub org audit log: {github_evidence}. HR access reviews: {hr_evidence}. Previous audit period gaps: {prior_gaps}.",
        [
          {
            field: "artifacts_collected",
            as: "artifacts_collected",
            type: "number",
          },
          { field: "criteria_covered", as: "criteria_covered", type: "number" },
          { field: "gap_count", as: "gap_count", type: "number" },
          {
            field: "coverage_percentage",
            as: "coverage_percentage",
            type: "number",
          },
          { field: "gap_details", as: "gap_details", type: "string" },
          { field: "evidence_index", as: "evidence_index", type: "string" },
        ],
      ),
      n("4", "hitApi", "AWS + GitHub + HR: Collect evidence artifacts", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://cloudtrail.{aws_region}.amazonaws.com/",
          headers: [
            { key: "Authorization", value: "AWS4-HMAC-SHA256 {aws_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "LookupAttributes",
              value:
                '[{"AttributeKey":"EventSource","AttributeValue":"iam.amazonaws.com"}]',
            },
            { key: "MaxResults", value: "50" },
          ],
          variables: [
            { name: "Events", customName: "aws_evidence", type: "string" },
            {
              name: "Events.length",
              customName: "aws_event_count",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Evidence mapping confidence", 680),
      sheetsNode(
        "6",
        "Sheets: Compile evidence index",
        840,
        "SOC2EvidenceIndex",
        '[["{case_id}","{artifacts_collected}","{criteria_covered}","{gap_count}","{coverage_percentage}","{evidence_index}","{started_at}"]]',
      ),
      approvalNode(
        "7",
        "Approval: Compliance lead review (low confidence)",
        840,
        "SOC2 evidence collection needs review. Artifacts: {artifacts_collected}. Criteria covered: {criteria_covered}. Gaps: {gap_count}. Coverage: {coverage_percentage}%. Gap details: {gap_details}. AI confidence: {ai_confidence}%.",
        72,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Evidence index updated in Sheets",
        1000,
        "GET",
        "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SOC2EvidenceIndex!A1:A",
        [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [{ name: "values.length", customName: "total_rows", type: "number" }],
      ),
      conditionNode(
        "9",
        "Check: Evidence index update verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log audit run metadata",
        1320,
        "SOC2AuditLog",
        '[["{case_id}","{artifacts_collected}","{criteria_covered}","{gap_count}","{coverage_percentage}","{gap_details}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Monthly collection gathers evidence from AWS CloudTrail, GitHub, and HR systems, uses AI to map artifacts to SOC2 TSC criteria and identify gaps, compiles evidence index in Sheets, routes uncertain mappings to compliance lead, and logs audit metadata.",
  },

  // ── 57. Elite: Device Provisioning Workflow ──
  "Elite: Device Provisioning Workflow": {
    nodes: [
      trigger("BambooHR: New hire onboarding webhook", "webhook_app", {
        source: "BambooHR",
        event: "employee.onboarded",
        howItStarts:
          "Fires when a new hire record is created in BambooHR with a start date within the next 14 days.",
        setup:
          "Configure BambooHR webhook for new hire events. Ensure payload includes employee name, email, department, role, location, and start date. IT asset inventory system must be accessible via API.",
        inputFields: [
          { name: "employee_name", type: "string", example: "Alex Johnson" },
          {
            name: "employee_email",
            type: "string",
            example: "alex.johnson@company.com",
          },
          { name: "department", type: "string", example: "Engineering" },
          { name: "role", type: "string", example: "Senior Software Engineer" },
          { name: "location", type: "string", example: "San Francisco" },
          { name: "start_date", type: "string", example: "2024-04-01" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Device provisioning",
        200,
        "Device Provisioning Workflow",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Determine equipment by role + location",
        360,
        "You are an IT asset management specialist. Determine the appropriate hardware and software provisioning package based on the employee role, department, and location. Consider: laptop spec (standard vs high-performance for engineering/design), monitors (1 for non-technical, 2 for technical), peripherals (keyboard, mouse, headset), software licenses (role-specific tools), and shipping logistics for remote locations. Return JSON with laptop_model, laptop_spec ('standard','high_performance','executive'), monitor_count (number), peripherals_list (comma-separated), software_licenses (comma-separated), shipping_method ('office_pickup','overnight','standard_ground'), estimated_cost (number), and provisioning_notes (any special considerations).",
        "New hire: {employee_name}. Role: {role}. Department: {department}. Location: {location}. Start date: {start_date}. Employee data: {employee_data}.",
        [
          { field: "laptop_model", as: "laptop_model", type: "string" },
          { field: "laptop_spec", as: "laptop_spec", type: "string" },
          { field: "monitor_count", as: "monitor_count", type: "number" },
          { field: "peripherals_list", as: "peripherals_list", type: "string" },
          {
            field: "software_licenses",
            as: "software_licenses",
            type: "string",
          },
          { field: "estimated_cost", as: "estimated_cost", type: "number" },
          {
            field: "provisioning_notes",
            as: "provisioning_notes",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch full employee profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_domain}/v1/employees/{employee_id}?fields=firstName,lastName,department,jobTitle,location,hireDate,workEmail",
          headers: [
            { key: "Authorization", value: "Basic {bamboo_auth}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "firstName", customName: "first_name", type: "string" },
            { name: "department", customName: "dept", type: "string" },
            { name: "jobTitle", customName: "job_title", type: "string" },
            { name: "location", customName: "office_location", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Provisioning recommendation confidence", 680),
      n("6", "hitApi", "Jira: Create IT provisioning ticket", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"IT"},"summary":"[Provisioning] {employee_name} — {role} starting {start_date}","description":"New hire device provisioning request.\\n\\nCase: {case_id}\\nEmployee: {employee_name}\\nRole: {role}\\nDepartment: {department}\\nLocation: {location}\\nStart date: {start_date}\\n\\nEquipment:\\n- Laptop: {laptop_model} ({laptop_spec})\\n- Monitors: {monitor_count}\\n- Peripherals: {peripherals_list}\\n- Software: {software_licenses}\\n\\nShipping: {shipping_method}\\nEst. cost: ${estimated_cost}\\nNotes: {provisioning_notes}","issuetype":{"name":"Task"},"priority":{"name":"High"},"labels":["provisioning","new-hire"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_ticket_id", type: "string" },
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: IT manager review (low confidence)",
        840,
        "Device provisioning needs IT review. Employee: {employee_name}. Role: {role}. Laptop: {laptop_model} ({laptop_spec}). Monitors: {monitor_count}. Software: {software_licenses}. Cost: ${estimated_cost}. Notes: {provisioning_notes}. AI confidence: {ai_confidence}%.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Jira provisioning tickets created",
        1000,
        "GET",
        "https://{jira_domain}/rest/api/3/issue/{jira_ticket_key}",
        [
          { key: "Authorization", value: "Basic {jira_auth}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          { name: "key", customName: "verified_ticket_key", type: "string" },
          {
            name: "fields.status.name",
            customName: "ticket_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Ticket creation verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log to inventory tracker",
        1320,
        "DeviceInventory",
        '[["{case_id}","{employee_name}","{role}","{department}","{location}","{laptop_model}","{laptop_spec}","{estimated_cost}","{jira_ticket_key}","{start_date}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triggers on new hire creation in BambooHR, uses AI to determine equipment package by role and location, auto-creates Jira provisioning tickets for high-confidence cases, routes unusual roles to IT manager, verifies ticket creation, and logs to inventory tracker.",
  },

  // ── 58. Elite: SaaS License Offboarding ──
  "Elite: SaaS License Offboarding": {
    nodes: [
      trigger("BambooHR: Employee termination event", "webhook_app", {
        source: "BambooHR",
        event: "employee.terminated",
        howItStarts:
          "Fires when an employee is marked as terminated in BambooHR, triggering SaaS license deprovisioning across all connected applications.",
        setup:
          "Configure BambooHR termination webhook. Ensure Okta and Google Workspace admin API access for license deprovisioning. Payload includes employee name, email, termination date, and department.",
        inputFields: [
          { name: "employee_name", type: "string", example: "Pat Wilson" },
          {
            name: "employee_email",
            type: "string",
            example: "pat.wilson@company.com",
          },
          { name: "termination_date", type: "string", example: "2024-03-22" },
          { name: "department", type: "string", example: "Marketing" },
          { name: "termination_type", type: "string", example: "voluntary" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: License offboarding",
        200,
        "SaaS License Offboarding",
      ),
      aiNodeV2(
        "3",
        "OpenAI: List active licenses + assess risk",
        360,
        "You are an IT offboarding specialist. Review the terminated employee's active licenses and access, and create a prioritized deprovisioning plan. Consider: admin-level accounts (highest priority), access to customer data, shared credentials, OAuth tokens, and data transfer needs. Return JSON with total_active_licenses (count), admin_accounts (comma-separated list of apps where user has admin), customer_data_access (comma-separated apps with customer data), deprovision_order (string listing apps in priority order), data_transfer_needed (boolean), data_transfer_apps (comma-separated or 'none'), risk_assessment ('standard','elevated','critical'), and offboarding_checklist (string with numbered steps).",
        "Terminated employee: {employee_name} ({employee_email}). Department: {department}. Type: {termination_type}. Okta apps: {okta_apps}. Google Workspace status: {gws_status}.",
        [
          {
            field: "total_active_licenses",
            as: "total_active_licenses",
            type: "number",
          },
          { field: "admin_accounts", as: "admin_accounts", type: "string" },
          {
            field: "customer_data_access",
            as: "customer_data_access",
            type: "string",
          },
          {
            field: "deprovision_order",
            as: "deprovision_order",
            type: "string",
          },
          {
            field: "data_transfer_needed",
            as: "data_transfer_needed",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
        ],
      ),
      n(
        "4",
        "hitApi",
        "Okta + Google Workspace: Fetch active assignments",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{okta_domain}/api/v1/users/{employee_email}/appLinks",
            headers: [
              { key: "Authorization", value: "SSWS {okta_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "length", customName: "okta_app_count", type: "number" },
              { name: "", customName: "okta_apps", type: "string" },
            ],
          },
        },
      ),
      confidenceGate("5", "Gate: Offboarding plan confidence", 680),
      n("6", "hitApi", "Okta: Deactivate user SSO access", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/users/{okta_user_id}/lifecycle/deactivate",
          headers: [
            { key: "Authorization", value: "SSWS {okta_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "deactivation_status",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: IT lead review for admin accounts",
        840,
        "License offboarding needs IT lead review. Employee: {employee_name}. Admin accounts: {admin_accounts}. Customer data access: {customer_data_access}. Data transfer needed: {data_transfer_needed}. Risk: {risk_assessment}. Total licenses: {total_active_licenses}. AI confidence: {ai_confidence}%.",
        8,
        400,
      ),
      verifyNode(
        "8",
        "Verify: User deprovisioned in Okta",
        1000,
        "GET",
        "https://{okta_domain}/api/v1/users/{employee_email}",
        [
          { key: "Authorization", value: "SSWS {okta_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "status",
            customName: "verified_user_status",
            type: "string",
          },
          {
            name: "profile.email",
            customName: "verified_email",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Deprovisioning verified",
        1160,
        "verified_user_status",
        "EQUALS",
        "DEPROVISIONED",
      ),
      sheetsNode(
        "10",
        "Sheets: Log license offboarding",
        1320,
        "LicenseOffboardLog",
        '[["{case_id}","{employee_name}","{employee_email}","{department}","{termination_type}","{total_active_licenses}","{admin_accounts}","{risk_assessment}","{verified_user_status}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triggers on employee termination, uses AI to inventory active licenses and prioritize deprovisioning by risk, auto-deactivates Okta SSO for standard cases, routes admin account offboarding to IT lead, verifies deprovisioning, and logs to license tracker.",
  },

  // ── 59. Elite: Third-Party Risk Assessor ──
  "Elite: Third-Party Risk Assessor": {
    nodes: [
      trigger("Webhook: New vendor risk assessment requested", "webhook_form", {
        source: "VendorPortal",
        event: "risk_assessment.requested",
        howItStarts:
          "Fires when a team requests a third-party risk assessment for a new vendor they want to engage.",
        setup:
          "Connect vendor portal form for risk assessment requests. Payload includes vendor name, website, data access type, and business justification. SecurityScorecard API access required for external scoring.",
        inputFields: [
          {
            name: "vendor_name",
            type: "string",
            example: "CloudSync Analytics",
          },
          { name: "vendor_domain", type: "string", example: "cloudsync.io" },
          {
            name: "data_classification",
            type: "string",
            example: "confidential",
          },
          {
            name: "requester_email",
            type: "string",
            example: "procurement@company.com",
          },
          {
            name: "business_justification",
            type: "string",
            example: "Need analytics platform for customer usage data",
          },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Third-party risk assessment",
        200,
        "Third-Party Risk Assessor",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Generate questionnaire + score risk",
        360,
        "You are a third-party risk management (TPRM) specialist. Based on the vendor profile and SecurityScorecard data, generate a tailored risk questionnaire and preliminary risk score. Consider: data classification level, security posture (SecurityScorecard grade), SOC2/ISO certifications, GDPR compliance, sub-processor risks, and business criticality. Return JSON with preliminary_risk_score (1-100, higher is more risky), risk_tier ('low','medium','high','critical'), security_grade (from SecurityScorecard), questionnaire_topics (comma-separated list of assessment areas), tailored_questions (string with 10 numbered questions specific to this vendor), required_certifications (comma-separated), assessment_deadline_days (number), and risk_summary (3 sentence overview).",
        "Vendor: {vendor_name} ({vendor_domain}). Data classification: {data_classification}. Justification: {business_justification}. SecurityScorecard data: {scorecard_data}.",
        [
          {
            field: "preliminary_risk_score",
            as: "preliminary_risk_score",
            type: "number",
          },
          { field: "risk_tier", as: "risk_tier", type: "string" },
          { field: "security_grade", as: "security_grade", type: "string" },
          {
            field: "tailored_questions",
            as: "tailored_questions",
            type: "string",
          },
          {
            field: "required_certifications",
            as: "required_certifications",
            type: "string",
          },
          { field: "risk_summary", as: "risk_summary", type: "string" },
        ],
      ),
      n(
        "4",
        "hitApi",
        "SecurityScorecard: Fetch vendor security posture",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.securityscorecard.io/companies/{vendor_domain}",
            headers: [
              { key: "Authorization", value: "Token {scorecard_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "grade", customName: "vendor_grade", type: "string" },
              { name: "score", customName: "vendor_score", type: "number" },
              {
                name: "industry",
                customName: "vendor_industry",
                type: "string",
              },
              {
                name: "name",
                customName: "scorecard_vendor_name",
                type: "string",
              },
            ],
          },
        },
      ),
      confidenceGate("5", "Gate: Risk assessment confidence", 680),
      emailNode(
        "6",
        "Email: Send questionnaire to vendor",
        840,
        "{vendor_contact_email}",
        "Security Assessment Questionnaire — {vendor_name} | {case_id}",
        "Dear {vendor_name} Team,\\n\\nAs part of our vendor risk management process, we require completion of the following security assessment.\\n\\nYour preliminary security grade: {security_grade}\\nAssessment deadline: {assessment_deadline_days} business days\\n\\nRequired certifications: {required_certifications}\\n\\n== Assessment Questions ==\\n{tailored_questions}\\n\\nPlease reply to this email with your completed responses, or upload them to our vendor portal.\\n\\nThank you,\\nSecurity & Compliance Team\\nRef: {case_id}",
      ),
      approvalNode(
        "7",
        "Approval: Security owner review (low confidence)",
        840,
        "Vendor risk assessment needs security review. Vendor: {vendor_name} ({vendor_domain}). Data classification: {data_classification}. Preliminary score: {preliminary_risk_score}/100. Risk tier: {risk_tier}. Grade: {security_grade}. Summary: {risk_summary}. AI confidence: {ai_confidence}%.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Questionnaire email sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={vendor_contact_email}&count=1",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Messages[0].Status",
            customName: "questionnaire_email_status",
            type: "string",
          },
          {
            name: "Messages[0].MessageID",
            customName: "questionnaire_message_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Questionnaire delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log to risk register",
        1320,
        "VendorRiskRegister",
        '[["{case_id}","{vendor_name}","{vendor_domain}","{data_classification}","{preliminary_risk_score}","{risk_tier}","{security_grade}","{required_certifications}","{ai_confidence}","{questionnaire_email_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes vendor risk assessment requests, enriches with SecurityScorecard data, uses AI to generate tailored questionnaires and score risk, auto-sends to vendors for high-confidence assessments, routes critical vendors to security review, verifies delivery, and logs to risk register.",
  },

  // ── 60. Elite: Policy Acknowledgment Enforcer ──
  "Elite: Policy Acknowledgment Enforcer": {
    nodes: [
      trigger("Webhook: New policy version published", "webhook_app", {
        source: "PolicyManager",
        event: "policy.published",
        howItStarts:
          "Fires when a new or updated company policy is published that requires employee acknowledgment.",
        setup:
          "Configure policy management tool webhook for publish events. Payload includes policy name, version, affected roles/departments, and acknowledgment deadline. Microsoft Graph API access required for employee directory.",
        inputFields: [
          {
            name: "policy_name",
            type: "string",
            example: "Information Security Policy v3.2",
          },
          { name: "policy_version", type: "string", example: "3.2" },
          {
            name: "policy_url",
            type: "string",
            example: "https://policies.company.com/infosec/v3.2",
          },
          { name: "affected_roles", type: "string", example: "all_employees" },
          { name: "ack_deadline_days", type: "number", example: "14" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Policy acknowledgment",
        200,
        "Policy Acknowledgment Enforcer",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Resolve target audience by role",
        360,
        "You are a compliance operations specialist. Determine the exact audience for this policy acknowledgment based on the affected roles specification and employee directory. Consider: department scoping, role-level requirements, contractor vs FTE distinctions, regional compliance requirements, and manager cascade notifications. Return JSON with total_employees_targeted (count), employee_segments (comma-separated groups), includes_contractors (boolean), regional_requirements (string noting any regional-specific addendums), notification_waves (number of waves for staged rollout, 1-3), wave_schedule (string describing rollout timing), acknowledgment_message (personalized email body for employees), and escalation_plan (string describing what happens for non-acknowledgment).",
        "Policy: {policy_name} v{policy_version}. Affected roles: {affected_roles}. Deadline: {ack_deadline_days} days. Employee directory data: {employee_list}.",
        [
          {
            field: "total_employees_targeted",
            as: "total_employees_targeted",
            type: "number",
          },
          {
            field: "employee_segments",
            as: "employee_segments",
            type: "string",
          },
          {
            field: "includes_contractors",
            as: "includes_contractors",
            type: "string",
          },
          {
            field: "notification_waves",
            as: "notification_waves",
            type: "number",
          },
          {
            field: "acknowledgment_message",
            as: "acknowledgment_message",
            type: "string",
          },
          { field: "escalation_plan", as: "escalation_plan", type: "string" },
        ],
      ),
      n("4", "hitApi", "Microsoft Graph: Fetch employee list by role", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.microsoft.com/v1.0/users?$filter=accountEnabled eq true&$select=displayName,mail,department,jobTitle&$top=999",
          headers: [
            { key: "Authorization", value: "Bearer {graph_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "value.length",
              customName: "employee_count",
              type: "number",
            },
            { name: "value", customName: "employee_list", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Audience resolution confidence", 680),
      emailNode(
        "6",
        "Email: Send acknowledgment requests",
        840,
        "{batch_email_list}",
        "Action Required: Acknowledge {policy_name} v{policy_version} by {ack_deadline}",
        "Dear Colleague,\\n\\n{acknowledgment_message}\\n\\nPolicy: {policy_name} (Version {policy_version})\\nReview the full policy: {policy_url}\\n\\nPlease acknowledge by clicking the link below within {ack_deadline_days} days:\\nhttps://policies.company.com/acknowledge/{case_id}\\n\\nNon-acknowledgment will result in escalation per the compliance escalation process.\\n\\nThank you,\\nCompliance Team\\nRef: {case_id}",
      ),
      approvalNode(
        "7",
        "Approval: Compliance review (low confidence)",
        840,
        "Policy acknowledgment targeting needs review. Policy: {policy_name} v{policy_version}. Targeted: {total_employees_targeted} employees. Segments: {employee_segments}. Contractors included: {includes_contractors}. Waves: {notification_waves}. AI confidence: {ai_confidence}%. Please verify audience and approve distribution.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Acknowledgment requests sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?count=1&offset=0",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "TotalCount",
            customName: "emails_sent_total",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "batch_send_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Batch send verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log policy acknowledgment audit",
        1320,
        "PolicyAckAudit",
        '[["{case_id}","{policy_name}","{policy_version}","{total_employees_targeted}","{employee_segments}","{includes_contractors}","{notification_waves}","{ai_confidence}","{emails_sent_total}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triggers on policy publication, resolves target audience from Microsoft Graph by role using AI, sends personalized acknowledgment requests for high-confidence targeting, routes uncertain audience scoping to compliance review, verifies delivery, and logs to audit sheet.",
  },
};
